import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import VideoPlayer from '../components/VideoPlayer';
import {
  useDoc,
  renameAsset,
  trashAsset,
  restoreAsset,
  vaporizeAsset,
  isTrashed,
  purgeDaysLeft,
  requestVideoProxy,
  getAssetDoc,
} from '../data/firestore';
import { getAssetPlaybackUrl } from '../data/uploadService';
import { useAuth } from '../AuthContext';
import { httpsify } from '../utils/url';
import { useNow, renderEtaLabel } from '../utils/eta';
import { InlineRename, DownloadButton, TrashControls } from '../components/AssetActions';
import JobFanoutGraph from '../components/JobFanoutGraph';
import FrameGrabber from '../components/FrameGrabber';
import PhotoViewer from '../components/PhotoViewer';
import ClipSoundtrackEditor from '../components/ClipSoundtrackEditor';
import EditTree from '../components/EditTree';
import { defaultSongChoice } from '../utils/viewerSongs';

export default function WatchAsset() {
  const { tripId, assetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const canEdit = !!user;
  const { doc: asset } = useDoc(
    tripId && assetId ? `trips/${tripId}/assets/${assetId}` : null
  );
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [grabbing, setGrabbing] = useState(false);
  const [grabStart, setGrabStart] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);

  // Arriving from the photo editor's "Re-grab from clip" opens the grabber at the
  // original frame so you can pick a new frame/crop from the full-res source.
  useEffect(() => {
    if (location.state?.grab) {
      setGrabStart(Number(location.state.grabTime) || 0);
      setGrabbing(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);
  // Viewer's soundtrack choice: undefined = use the owner's default.
  const [songChoice, setSongChoice] = useState(undefined);
  // Playback quality for non-RED clips that have both a fast proxy and a
  // streamable original master. Defaults to the proxy (fast load + scrub); the
  // viewer can switch to the full-quality master on demand.
  const [quality, setQuality] = useState('proxy'); // 'proxy' | 'original'
  const s3Key = asset && asset.s3Key;
  // RED R3D can't be streamed directly; play the decoded HLS proxy off the CDN.
  const isRed = !!asset?.isRawVideo;
  const proxyReady = asset?.proxy?.status === 'ready' && !!asset?.proxyHlsUrl;
  const vproxy = asset?.vproxy || {};
  // The 1080p proxy can live inline on this asset (asset.proxyHlsUrl) OR as a
  // separate sibling "— 1080p stream" asset linked via vproxy.proxyAssetId. Load
  // the sibling so we can play it in place and default to it like RED proxies do.
  const proxyElsewhereId = !isRed && !asset?.isProxy ? vproxy.proxyAssetId : null;
  const [siblingProxy, setSiblingProxy] = useState(null);
  useEffect(() => {
    setSiblingProxy(null);
    if (!proxyElsewhereId || !tripId) return;
    let alive = true;
    getAssetDoc(tripId, proxyElsewhereId)
      .then((d) => alive && setSiblingProxy(d))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, proxyElsewhereId]);

  // Unified 1080p proxy URL: inline first, else the sibling proxy's.
  const proxyUrl = isRed
    ? (proxyReady ? asset?.proxyHlsUrl : '')
    : (asset?.proxyHlsUrl || siblingProxy?.proxyHlsUrl || '');
  // Non-RED sources: a 1080p HLS proxy (fast) and/or a streamable original.
  const hasProxy = !isRed && !!proxyUrl;
  const hasOriginal = !isRed && !!s3Key;
  // A proxy is known to exist but its URL hasn't loaded yet (sibling still
  // fetching) — hold off loading the heavy original so the default lands on 1080p.
  const proxyPending = !!proxyElsewhereId && !proxyUrl;
  const vproxyBusy = !isRed && (vproxy.status === 'queued' || vproxy.status === 'rendering');
  const wantOriginal = quality === 'original';
  // Which source actually plays. RED always uses its proxy. Non-RED prefers the
  // proxy unless the viewer asked for the original (and one exists).
  const playProxy = isRed ? proxyReady : hasProxy && !(wantOriginal && hasOriginal);
  const proxyBusy = asset?.proxy?.status === 'queued' || asset?.proxy?.status === 'rendering';
  const now = useNow((isRed && proxyBusy) || vproxyBusy);
  // Frame rate of whatever stream is actually playing — used for exact
  // frame-by-frame arrow stepping. Prefer the proxy's fps when streaming the
  // proxy, else the source/container average. captureFps is intentionally
  // ignored (it's the slow-mo capture rate, not the playback timeline rate).
  const playbackFps =
    Number(
      (playProxy && (asset?.proxyFps || siblingProxy?.proxyFps)) ||
        asset?.srcFps ||
        asset?.fpsAvg ||
        asset?.metadata?.video?.fpsAvg ||
        asset?.fpsContainer ||
        asset?.metadata?.video?.fpsContainer ||
        0
    ) || 30;
  // Real-life speed-up for slow-motion footage: RED reports record_framerate in
  // redMeta; other cameras expose captureFps. If the recording rate is well
  // above the playback timeline, the clip plays in slow motion and this is the
  // multiplier that restores natural speed.
  const recordRate = isRed
    ? Number(asset?.redMeta?.recordFps) || 0
    : Number(asset?.captureFps) || 0;
  const trueSpeed =
    Number(asset?.redMeta?.trueSpeedX) ||
    (recordRate > 0 && playbackFps > 0 && recordRate > playbackFps * 1.05
      ? recordRate / playbackFps
      : 0);
  const sizeBytes = asset?.sizeBytes || 0;
  // A multi-GB master (e.g. ProRes .mov) can't be streamed progressively in a
  // browser. While its 1080p proxy renders, show progress instead of silently
  // failing to load the giant original.
  const bigMaster = sizeBytes > 3e9;
  const needsProxyWait = !isRed && !playProxy && bigMaster && vproxyBusy;

  useEffect(() => {
    let alive = true;
    setUrl(null);
    setErr(null);
    if (isRed) {
      // Proxy is a public CDN HLS playlist — no presigning needed.
      if (proxyReady) setUrl(httpsify(asset.proxyHlsUrl));
    } else if (playProxy) {
      setUrl(httpsify(proxyUrl));
    } else if (proxyPending && !wantOriginal) {
      // A 1080p proxy exists but its URL is still loading (sibling asset) — wait
      // for it instead of pulling the heavy original, so the default stays 1080p.
    } else if (needsProxyWait) {
      // Don't try to load the unplayable giant master; the wait screen shows
      // proxy progress and the player swaps in once the proxy is ready.
    } else if (s3Key) {
      getAssetPlaybackUrl(s3Key)
        .then((u) => alive && setUrl(u))
        .catch((e) => alive && setErr(e.message));
    }
    return () => {
      alive = false;
    };
  }, [s3Key, isRed, playProxy, proxyReady, asset?.proxyHlsUrl, proxyUrl, proxyPending, wantOriginal, needsProxyWait]);

  // Re-sign on demand so the player can recover from an expired presigned URL
  // (the GET link lasts 6h) without the viewer losing their place. The CDN HLS
  // proxy never expires, so this is only used for progressive raw playback.
  const refreshSrc = useCallback(
    () => (!playProxy && s3Key ? getAssetPlaybackUrl(s3Key) : Promise.resolve(null)),
    [s3Key, playProxy]
  );

  // ---- Unified quality model for non-RED video ----------------------------
  // One control handles every case: switch in place (inline OR sibling proxy),
  // or kick off a transcode when no proxy exists yet. The sibling proxy now
  // plays in place (its URL is loaded above), so big masters default to 1080p.
  const isProxyAsset = !isRed && !!asset?.isProxy;
  const has1080 = isProxyAsset || hasProxy || !!proxyElsewhereId;
  const showing1080 = isProxyAsset || (playProxy && !wantOriginal);
  const canShowOriginal = hasOriginal || (isProxyAsset && !!asset?.proxyOf);

  const goOriginal = useCallback(() => {
    if (isProxyAsset) {
      if (asset?.proxyOf) navigate(`/watch/asset/${tripId}/${asset.proxyOf}`);
    } else {
      setQuality('original');
    }
  }, [isProxyAsset, asset?.proxyOf, navigate, tripId]);

  const go1080 = useCallback(() => {
    if (hasProxy || proxyElsewhereId) setQuality('proxy');
    else if (canEdit && !vproxyBusy) requestVideoProxy(tripId, asset.id);
  }, [hasProxy, proxyElsewhereId, vproxyBusy, tripId, asset?.id, canEdit]);

  if (asset === undefined) return <div className="cs-page">Loading…</div>;
  if (asset === null) return <div className="cs-page">Footage not found.</div>;

  const isPhoto = asset.kind === 'photo';
  const proxyStatus = asset.proxy?.status;
  const redPending = isRed && !proxyReady;

  // Viewer-selectable soundtrack for this clip. The chosen song overlays at
  // normal speed (see VideoPlayer); 'orig' keeps the clip's own audio.
  const viewerSongs = asset.viewerSongs || [];
  const hasViewerSongs = viewerSongs.length > 0;
  const songSel = songChoice !== undefined ? songChoice : defaultSongChoice(viewerSongs, asset.viewerSongDefault);
  const chosenSong =
    hasViewerSongs && songSel !== 'orig' ? viewerSongs.find((s) => s.id === songSel) || null : null;

  return (
    <div className="cs-player-wrap">
      {err ? (
        <div className="cs-page">
          <div className="cs-empty">Couldn’t load this file: {err}</div>
        </div>
      ) : redPending ? (
        <div className="cs-page">
          <div className="cs-empty">
            {proxyStatus === 'error'
              ? `Stream proxy failed: ${asset.proxy?.message || 'unknown error'}. You can still download the original .RDC.`
              : `${renderEtaLabel(asset.proxy, now, 'Decoding RED clip')} — download the original .RDC below meanwhile.`}
          </div>
          {proxyBusy && asset.proxy?.jobId && (
            <div style={{ maxWidth: 1100, margin: '16px auto 0' }}>
              <JobFanoutGraph jobId={asset.proxy.jobId} height={380} title="RED decode · live fan-out" />
            </div>
          )}
        </div>
      ) : needsProxyWait ? (
        <div className="cs-page">
          <div className="cs-empty">
            {vproxy.status === 'error'
              ? `Stream proxy failed: ${vproxy.message || 'unknown error'}. Download the original (${(sizeBytes / 1e9).toFixed(1)} GB) below.`
              : `${renderEtaLabel(vproxy, now, 'Optimizing for streaming')} — this ${(sizeBytes / 1e9).toFixed(1)} GB master is too large to stream directly in a browser, so the 1080p version will play here automatically once it’s ready. You can download the original below meanwhile.`}
          </div>
        </div>
      ) : !url ? (
        <div className="cs-page">
          <div className="cs-empty">Preparing stream…</div>
        </div>
      ) : isPhoto ? (
        (() => {
          const hx = (u) => (u ? httpsify(u) : '');
          const derivatives = asset.derivatives || {};
          // Fast preview (capped JPEG) for the inline image + viewer placeholder;
          // the full-resolution source for true 100% pixel-peeping. RAW originals
          // aren't browser-viewable, so fall back to a developed full-res JPEG.
          const preview = hx(asset.displayUrl) || hx(derivatives.magazine) || asset.thumbUrl || url;
          const full = asset.isRaw ? (hx(derivatives.fulljpeg) || hx(asset.displayUrl) || url) : url;
          return (
            <>
              <img
                src={preview || url}
                alt={asset.name}
                onClick={() => setViewerOpen(true)}
                title="Click to view full resolution (Fit / 100% pixel-peep)"
                style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain', background: '#000', cursor: 'zoom-in' }}
              />
              {viewerOpen && (
                <PhotoViewer
                  src={full}
                  placeholder={preview}
                  name={asset.name}
                  downloadHref={full}
                  onClose={() => setViewerOpen(false)}
                />
              )}
            </>
          );
        })()
      ) : (
        <div className="cs-player-stage">
          <VideoPlayer src={url} type={playProxy ? 'hls' : 'mp4'} getFreshSrc={refreshSrc} fps={playbackFps} song={chosenSong} trueSpeed={trueSpeed} />
          {!isRed && (
            <div className="cs-vq" role="group" aria-label="Playback quality">
              <span
                className="cs-segment cs-segment-sm cs-quality-seg"
                title={
                  has1080
                    ? 'Switch between the full-quality original master and the fast 1080p version'
                    : 'Original plays now. Make a 1080p version so this clip loads & scrubs quickly (original stays archival).'
                }
              >
                <button
                  className={!showing1080 ? 'on' : ''}
                  onClick={goOriginal}
                  disabled={!canShowOriginal}
                >
                  Original
                </button>
                {(has1080 || canEdit) && (
                  <button
                    className={showing1080 ? 'on' : ''}
                    onClick={go1080}
                    disabled={vproxyBusy && !has1080}
                  >
                    {has1080 ? '1080p · fast' : vproxyBusy ? 'Optimizing…' : '⚡ Make 1080p'}
                  </button>
                )}
              </span>
              {vproxyBusy && !has1080 && (
                <span className="cs-quality-eta">{renderEtaLabel(vproxy, now, 'Optimizing')}</span>
              )}
            </div>
          )}
        </div>
      )}
      <div className="cs-player-info">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => navigate(-1)}>
            ← Back
          </button>
          {isPhoto && url && (
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={() => setViewerOpen(true)}
              title="View at full resolution — Fit to screen or 100% pixel-peep"
            >
              ⤢ View full size
            </button>
          )}
          {canEdit && (
            <>
              <DownloadButton s3Key={asset.s3Key} name={asset.name} parts={asset.parts} title="Download original" />
              {!isPhoto && !redPending && !needsProxyWait && (
                <>
                  <button
                    className="cs-btn cs-btn-ghost cs-btn-sm"
                    onClick={() => setGrabbing(true)}
                    title="Save a full-resolution still from this clip to the trip's photos"
                  >
                    🖼 Grab frame
                  </button>
                  <button
                    className="cs-btn cs-btn-primary cs-btn-sm"
                    onClick={() => navigate(`/clip/${tripId}/${asset.id}/color`)}
                    title="Open the side-by-side color & light editor for this clip"
                  >
                    🎨 Edit color &amp; light
                  </button>
                  <button
                    className="cs-btn cs-btn-ghost cs-btn-sm"
                    onClick={() => navigate(`/clip-grade/${tripId}/${asset.id}`)}
                    title="Per-clip grading session: review proofs, give feedback, then bake every frame"
                  >
                    🎚 Grade session
                  </button>
                </>
              )}
              {isTrashed(asset) ? (
                <TrashControls
                  trashed
                  daysLeft={purgeDaysLeft(asset)}
                  what={`“${asset.name}”`}
                  onRestore={() => restoreAsset(tripId, asset.id)}
                  onVaporize={async () => {
                    await vaporizeAsset(tripId, asset);
                    navigate(-1);
                  }}
                />
              ) : (
                <TrashControls what={`“${asset.name}”`} onTrash={() => trashAsset(tripId, asset.id)} />
              )}
            </>
          )}
        </div>
        <div style={{ marginTop: 16 }}>
          {canEdit ? (
            <InlineRename
              name={asset.name}
              onSave={(n) => renameAsset(tripId, asset.id, n)}
              style={{ fontSize: 28, fontWeight: 700 }}
            />
          ) : (
            <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>{asset.name}</h1>
          )}
        </div>
        <div className="cs-hero-meta">
          {isPhoto
            ? 'Photo'
            : isRed
            ? 'RED R3D · streaming proxy'
            : asset.isProxy
            ? `1080p streaming proxy${asset.durationSec ? '' : ''}`
            : playProxy
            ? `Raw footage · playing 1080p proxy${hasOriginal ? ' (switch to Original above)' : ''}`
            : `Raw footage · original master${asset.srcW ? ` · ${asset.srcW}×${asset.srcH}` : ''}`}
        </div>
        {isPhoto && (
          <div style={{ marginTop: 16, maxWidth: 420 }}>
            <EditTree
              editTree={asset.editTree}
              manifest={asset.editManifest}
              editTreeStatus={asset.editTreeStatus}
              tripId={tripId}
              assetId={asset.id}
            />
          </div>
        )}
        {!isPhoto && hasViewerSongs && (
          <div style={{ marginTop: 16 }}>
            <div className="cs-help" style={{ marginBottom: 6 }}>Soundtrack</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {viewerSongs.map((s) => (
                <button
                  key={s.id}
                  className={`cs-btn cs-btn-sm ${songSel === s.id ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                  onClick={() => setSongChoice(s.id)}
                >
                  ♪ {s.label}
                </button>
              ))}
              <button
                className={`cs-btn cs-btn-sm ${songSel === 'orig' ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                onClick={() => setSongChoice('orig')}
              >
                Original audio
              </button>
            </div>
          </div>
        )}
        {!isPhoto && canEdit && <ClipSoundtrackEditor tripId={tripId} asset={asset} />}
      </div>
      {grabbing && (
        <FrameGrabber asset={asset} tripId={tripId} startTime={grabStart} onClose={() => { setGrabbing(false); setGrabStart(0); }} />
      )}
    </div>
  );
}
